import classNames from 'classnames';
import { useMemo } from 'react';
import { Column, useTable, useSortBy, PluginHook } from 'react-table';
import styles from './content-panel-table.module.scss';

export interface ContentPanelTableProps<T> {
	columns: ContentPanelTablePropsColumn<T>[];
	rows: T[];
	fullWidth?: boolean;

	sortable?: boolean;
}
export interface ContentPanelTablePropsColumn<T> {
	id: string;
	title: string;
	getValue: (row: T) => any;
	renderCell?: (row: T) => JSX.Element | string;

	cellClassName?: string;
	headerClassName?: string;

	defaultSorting?: ContentPanelTablePropsColumnDefaultSorting;
	sortType?: ContentPanelTablePropsColumnSortType;
}

export enum ContentPanelTablePropsColumnDefaultSorting {
	ASC = "ASC",
	DESC = "DESC"
}

export enum ContentPanelTablePropsColumnSortType {
	STRING = "string",
	NUMBER = "number",
	BASIC = "basic"
}
export const ContentPanelTable = <T extends object>(props: ContentPanelTableProps<T>) => {
	const fullWidth = typeof props.fullWidth === "undefined" ? true : props.fullWidth!

	const pluggins = useMemo(() => {
		const { sortable = false } = props;

		const pluggins: PluginHook<T>[] = [];
		sortable && pluggins.push(useSortBy);

		return pluggins;
	}, [props.sortable]);

	const tableColumns = useMemo(
		() => props.columns.map(toTableColumn),
		[props.columns]);

	const tableInitialState: any = useMemo(() => {
		return {
			sortBy: props.columns
				.filter(column => column.defaultSorting)
				.map(column => ({ id: column.id, desc: column.defaultSorting == ContentPanelTablePropsColumnDefaultSorting.DESC }))
		}
	}, []);

	const { getTableProps, getTableBodyProps, rows, prepareRow, headers } = useTable(
		{ columns: tableColumns, data: props.rows, initialState: tableInitialState },
		...pluggins);

	if (rows.length === 0) {
		return (
			<div className={styles.container}>
				<span className={styles.no_data}>Нет данных</span>
			</div>
		)
	}
	else {
		return (
			<div className={styles.container}>
				<table {...getTableProps({ className: fullWidth ? styles.full_width : '' })}>
					<thead>
						<tr className={styles.header_row}>
							{
								headers.map(header => 
								{
									const getSortByToggleProps = (header as any).getSortByToggleProps;

									const extraClassName = (header as any).headerClassName;
									const headerSortProps = getSortByToggleProps ? getSortByToggleProps() : { };
									const headerProps = header.getHeaderProps();

									const effectiveHeaderProps: any = { ...headerProps, ...headerSortProps };
									effectiveHeaderProps.className = classNames(effectiveHeaderProps.className, extraClassName);

									const { isSorted, isSortedDesc }: { isSorted: boolean, isSortedDesc: boolean } = header as any;

									return (
										<th {...effectiveHeaderProps}>
											{ header.render('Header') }
											{ isSorted
												? isSortedDesc
													? <i className={classNames("fas fa-chevron-down", styles.sort)}></i>
													: <i className={classNames("fas fa-chevron-up", styles.sort)}></i>
												: null}
										</th>
									);
								})
							}
						</tr>
					</thead>
					<tbody {...getTableBodyProps()}>
						{
							rows.map(row => {
								prepareRow(row);

								return (
									<tr {...row.getRowProps()}>
										{
											row.cells.map(cell => (
												<td {...cell.getCellProps({ className: classNames(styles.data_cell, (cell.column as any).cellClassName) })}>
													{ cell.render('Cell')}
												</td>
											))
										}
									</tr>
								)
							})
						}
					</tbody>
				</table>
			</div>
		);
	}
}

function toTableColumn<T extends object>(column: ContentPanelTablePropsColumn<T>): Column<T> {
	const options: any = {};
	options.id = column.id;
	options.Header = column.title;
	options.accessor = column.getValue;

	if (column.renderCell !== undefined) {
		const renderCell = column.renderCell!;
		options.Cell = (data: any) => renderCell(data.row.original as T);
	}
	if (column.cellClassName !== undefined) {
		options.cellClassName = column.cellClassName;
	}
	if (column.headerClassName !== undefined) {
		options.headerClassName = column.headerClassName;
	}
	if (column.sortType !== undefined) {
		options.sortType = column.sortType;
	}

	return options;
}
